// src/components/SmartFlow/Nodes/HTTP_Request/HttpRequestConfig.jsx

import React, { useState, useEffect } from "react";
import KeyValueEditor from "../../../shared/KeyValueEditor";
import "./HttpRequestConfig.css"; // crie esse CSS para estilizar como o n8n

const HttpRequestConfig = ({
  config,
  nodeMetadata,
  onChange,
  validationErrors = {},
  inputData,
}) => {
  const [localConfig, setLocalConfig] = useState(config || {});
  const [activeTab, setActiveTab] = useState("parameters");
  const DEFAULT_METHOD = "GET";

  // >>> NOVO: estado só para o texto do JSON body
  const [jsonBodyText, setJsonBodyText] = useState(() => {
    const initial = config?.body_json;
    if (typeof initial === "string") return initial;
    return JSON.stringify(initial || {}, null, 2);
  });

  useEffect(() => {
    setLocalConfig(config || {});
  }, [config]);

  useEffect(() => {
  if (!localConfig?.method) {
      const next = { ...localConfig, method: DEFAULT_METHOD };
      setLocalConfig(next);
      onChange(next);
    }
  }, [localConfig?.method]); 

  // mantém jsonBodyText sincronizado quando body_json vier de fora
  useEffect(() => {
    const value = localConfig.body_json;
    if (typeof value === "string") {
      setJsonBodyText(value);
    } else {
      setJsonBodyText(JSON.stringify(value || {}, null, 2));
    }
  }, [localConfig.body_json]);

  const schema = nodeMetadata?.config_schema || {};

  const updateConfig = (field, value) => {
    const newConfig = {
      ...localConfig,
      [field]: value,
    };
    setLocalConfig(newConfig);
    onChange(newConfig);
  };

  const getFieldSchema = (name) => schema[name] || {};

  const hasError = (field) => Boolean(validationErrors[field]);

  const renderError = (field) =>
    hasError(field) ? (
      <div className="http-config__error-text">
        {validationErrors[field]}
      </div>
    ) : null;

  // ======= FIELD RENDER HELPERS =======

  const renderSelect = (field, options, placeholder) => {
    const fieldSchema = getFieldSchema(field);
    const value =
      localConfig[field] ??
      fieldSchema.default ??
      (placeholder ? "" : options[0]);
    return (
      <div className="http-config__field">
        <label className="http-config__label">
          {fieldSchema.description || field}
          {fieldSchema.required && (
            <span className="http-config__label-required">*</span>
          )}
        </label>
        <select
          className={`http-config__input ${
            hasError(field) ? "http-config__input--error" : ""
          }`}
          value={localConfig.method}
          onChange={(e) => updateConfig(field, e.target.value)}
        >
          {placeholder && <option value="">{placeholder}</option>}
          {options.map((opt) => (
            <option key={opt} value={opt}>
              {opt}
            </option>
          ))}
        </select>
        {renderError(field)}
      </div>
    );
  };

  const renderTextInput = (field, type = "text", placeholder) => {
    const fieldSchema = getFieldSchema(field);
    const value = localConfig[field] ?? fieldSchema.default ?? "";
    return (
      <div className="http-config__field">
        <label className="http-config__label">
          {fieldSchema.description || field}
          {fieldSchema.required && (
            <span className="http-config__label-required">*</span>
          )}
        </label>
        <input
          type={type}
          className={`http-config__input ${
            hasError(field) ? "http-config__input--error" : ""
          }`}
          value={value}
          placeholder={
            placeholder || fieldSchema.placeholder || `Enter ${field}`
          }
          onChange={(e) => updateConfig(field, e.target.value)}
        />
        {renderError(field)}
      </div>
    );
  };

  const renderToggleSection = (label, fieldEnabled, children) => {
    const enabled = Boolean(localConfig[fieldEnabled]);
    return (
      <div className="http-config__toggle-section">
        <div className="http-config__toggle-header">
          <span className="http-config__toggle-title">{label}</span>
          <label className="http-config__switch">
            <input
              type="checkbox"
              checked={enabled}
              onChange={(e) => updateConfig(fieldEnabled, e.target.checked)}
            />
            <span className="http-config__slider" />
          </label>
        </div>
        {enabled && <div className="http-config__toggle-body">{children}</div>}
      </div>
    );
  };

  // ======= SEÇÕES “PARAMETERS” (como no n8n) =======

  const renderParametersTab = () => {
    // METHOD
    const methodEnum =
      getFieldSchema("method").enum || [
        "GET",
        "POST",
        "PUT",
        "PATCH",
        "DELETE",
        "HEAD",
        "OPTIONS",
      ];

    // URL
    const urlSchema = getFieldSchema("url");
    const urlValue = localConfig.url ?? urlSchema.default ?? "";
    const urlError = hasError("url");

    // Auth
    const authEnum =
      getFieldSchema("authentication").enum || [
        "None",
        "Basic",
        "Bearer",
        "API Key",
      ];

    return (
      <div className="http-config__tab-content">
        {/* Method + Execute Step button wrapper */}
        <div className="http-config__header-row">
          <div className="http-config__field http-config__field--method">
            <label className="http-config__label">Method</label>
            <select
              className="http-config__input"
              value={localConfig.method || methodEnum[0]}
              onChange={(e) => updateConfig("method", e.target.value)}
            >
              {methodEnum.map((m) => (
                <option key={m} value={m}>
                  {m}
                </option>
              ))}
            </select>
          </div>

        </div>

        {/* URL */}
        <div className="http-config__field">
          <label className="http-config__label">URL</label>
          <input
            type="text"
            className={`http-config__input ${
              urlError ? "http-config__input--error" : ""
            }`}
            value={urlValue}
            onChange={(e) => updateConfig("url", e.target.value)}
            placeholder={urlSchema.placeholder || "http://example.com/index.html"}
          />
        {urlError && <span className="http-config__url-warning">!</span>}
          {renderError("url")}
        </div>

        {/* Authentication */}
        <div className="http-config__field">
          <label className="http-config__label">Authentication</label>
          <select
            className="http-config__input"
            value={localConfig.authentication || "None"}
            onChange={(e) => updateConfig("authentication", e.target.value)}
          >
            {authEnum.map((a) => (
              <option key={a} value={a.toLowerCase()}>
                {a}
              </option>
            ))}
          </select>
        </div>

        {/* Query Parameters */}
        {renderToggleSection("Send Query Parameters", "enable_query_params", (
          <KeyValueEditor
            value={
              typeof localConfig.query_params === "object"
                ? localConfig.query_params
                : {}
            }
            onChange={(val) => updateConfig("query_params", val)}
            placeholder={{ key: "limit", value: "10" }}
            allowExpressions
          />
        ))}

        {/* Headers */}
        {renderToggleSection("Send Headers", "enable_headers", (
          <KeyValueEditor
            value={
              typeof localConfig.headers === "object"
                ? localConfig.headers
                : {}
            }
            onChange={(val) => updateConfig("headers", val)}
            placeholder={{ key: "Content-Type", value: "application/json" }}
            allowExpressions
          />
        ))}

        {/* Body */}
        {renderToggleSection("Send Body", "enable_body", (
          <>
            <div className="http-config__field">
              <label className="http-config__label">Body Type</label>
              <select
                className="http-config__input"
                value={localConfig.body_type || "none"}
                onChange={(e) => updateConfig("body_type", e.target.value)}
              >
                <option value="none">None</option>
                <option value="json">JSON</option>
                <option value="form">Form-Data</option>
                <option value="raw">Raw</option>
              </select>
            </div>

            {/* JSON body */}
            {localConfig.body_type === "json" && (
              <div className="http-config__field">
                <label className="http-config__label">JSON Body</label>
                <textarea
                  className="http-config__textarea"
                  rows={6}
                  value={jsonBodyText}
                  onChange={(e) => {
                    const text = e.target.value;
                    setJsonBodyText(text);

                    // tenta converter para objeto; só atualiza body_json se for válido
                    try {
                      const parsed = text ? JSON.parse(text) : {};
                      updateConfig("body_json", parsed);
                    } catch (err) {
                      // JSON inválido: não atualiza o body_json,
                      // assim o backend continua recebendo um objeto válido
                      // (último JSON parseado com sucesso).
                      console.warn("Invalid JSON body:", err);
                    }
                  }}
                  placeholder='{"key": "value"}'
                />
              </div>
            )}

            {/* Form body */}
            {localConfig.body_type === "form" && (
              <KeyValueEditor
                value={
                  typeof localConfig.body_form === "object"
                    ? localConfig.body_form
                    : {}
                }
                onChange={(val) => updateConfig("body_form", val)}
                placeholder={{ key: "field", value: "value" }}
                allowExpressions
              />
            )}

            {/* Raw body */}
            {localConfig.body_type === "raw" && (
              <>
                <div className="http-config__field">
                  <label className="http-config__label">
                    Content-Type
                  </label>
                  <input
                    type="text"
                    className="http-config__input"
                    value={localConfig.body_raw_content_type || ""}
                    onChange={(e) =>
                      updateConfig("body_raw_content_type", e.target.value)
                    }
                    placeholder="text/plain"
                  />
                </div>
                <div className="http-config__field">
                  <label className="http-config__label">Body</label>
                  <textarea
                    className="http-config__textarea"
                    rows={6}
                    value={localConfig.body_raw || ""}
                    onChange={(e) =>
                      updateConfig("body_raw", e.target.value)
                    }
                    placeholder="Raw body content"
                  />
                </div>
              </>
            )}
          </>
        ))}

        {/* Options */}
        <div className="http-config__options-section">
          <div className="http-config__options-title">Options</div>
          <div className="http-config__options-row">
            <select
              className="http-config__input http-config__options-select"
              value={localConfig.selected_option || ""}
              onChange={(e) => updateConfig("selected_option", e.target.value)}
            >
              <option value="">No properties</option>
              <option value="timeout">Timeout</option>
              <option value="follow_redirects">Follow redirects</option>
              <option value="verify_ssl">Verify SSL</option>
            </select>
            <button
              type="button"
              className="http-config__options-add-btn"
              onClick={() => {
                // aqui você pode abrir uma UI mais avançada se quiser
                console.log("Add option clicked");
              }}
            >
              Add option
            </button>
          </div>
        </div>

        {/* Hint final igual ao n8n */}
        <div className="http-config__hint">
          You can view the raw requests this node makes in your browser&apos;s
          developer console
        </div>
      </div>
    );
  };

  // ======= ABA SETTINGS (básica, similar n8n) =======
  const renderSettingsTab = () => (
    <div className="http-config__tab-content http-config__tab-content--secondary">
      <div className="http-config__field">
        <label className="http-config__label">Timeout (ms)</label>
        <input
          type="number"
          className="http-config__input"
          value={localConfig.timeout || ""}
          onChange={(e) =>
            updateConfig("timeout", e.target.value ? Number(e.target.value) : "")
          }
          placeholder="e.g. 30000"
        />
      </div>

      <div className="http-config__field">
        <label className="http-config__label">Verify SSL certificates</label>
        <label className="http-config__switch">
          <input
            type="checkbox"
            checked={localConfig.verify_ssl ?? true}
            onChange={(e) => updateConfig("verify_ssl", e.target.checked)}
          />
          <span className="http-config__slider" />
        </label>
      </div>

      <div className="http-config__field">
        <label className="http-config__label">
          Fail workflow on error status code
        </label>
        <label className="http-config__switch">
          <input
            type="checkbox"
            checked={localConfig.fail_on_error_status ?? true}
            onChange={(e) =>
              updateConfig("fail_on_error_status", e.target.checked)
            }
          />
          <span className="http-config__slider" />
        </label>
      </div>
    </div>
  );

  // ======= ABA DOCS (placeholder simples) =======
  const renderDocsTab = () => (
    <div className="http-config__tab-content http-config__tab-content--secondary">
      <p className="http-config__docs-text">
        HTTP Request node docs will appear here.  
        Use this node to call external APIs using GET, POST, PUT, DELETE and
        other HTTP methods.
      </p>
    </div>
  );

  // ======= RENDER FINAL =======
  return (
    <div className="http-config">
      {/* Tabs superiores */}
      <div className="http-config__tabs">
        <button
          type="button"
          className={`http-config__tab ${
            activeTab === "parameters" ? "http-config__tab--active" : ""
          }`}
          onClick={() => setActiveTab("parameters")}
        >
          Parameters
        </button>
        <button
          type="button"
          className={`http-config__tab ${
            activeTab === "settings" ? "http-config__tab--active" : ""
          }`}
          onClick={() => setActiveTab("settings")}
        >
          Settings
        </button>
        <button
          type="button"
          className={`http-config__tab ${
            activeTab === "docs" ? "http-config__tab--active" : ""
          }`}
          onClick={() => setActiveTab("docs")}
        >
          Docs
        </button>
      </div>

      {/* Pequena descrição do node se o backend mandar */}
      {nodeMetadata?.description && (
        <div className="http-config__description">
          {nodeMetadata.description}
        </div>
      )}

      {activeTab === "parameters" && renderParametersTab()}
      {activeTab === "settings" && renderSettingsTab()}
      {activeTab === "docs" && renderDocsTab()}

      {/* Sumário de erros globais (se quiser manter) */}
      {Object.keys(validationErrors).length > 0 && (
        <div className="http-config__errors-summary">
          <strong>Please fix the following errors:</strong>
          <ul>
            {Object.values(validationErrors).map((err, idx) => (
              <li key={idx}>{err}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default HttpRequestConfig;


// {
//   "id": 1,
//   "title": "Título atualizado",
//   "body": "Novo conteúdo",
//   "userId": 1
// }